/**
 * The shared vocabulary of an impact preview.
 *
 * Move, copy, folder-delete and file-remove all answer the same question —
 * who gains, who loses, who keeps — and they must answer it in the same
 * shape and the same words. Written once here so the four surfaces cannot
 * drift into describing the same consequence differently.
 */

import type { ReactNode } from 'react';
import type { FolderDeleteImpact, ImpactPerson, ImpactSubject } from '../types';

/** How a line reads: something taken away, something granted, or unchanged. */
export type ImpactTone = 'lose' | 'gain' | 'keep';

interface RowProps {
  tone?: ImpactTone;
  /** The sentence — "3 agents lose reach". Omit `items` for a bare line. */
  head: ReactNode;
  /** The names behind the sentence; already localized by the caller. */
  items?: string[];
}

export function ImpactRow({ tone, head, items }: RowProps) {
  return (
    <div className={`vault-impact-row${tone ? ` ${tone}` : ''}`}>
      {items && items.length
        ? <><div className="vault-impact-head">{head}</div>
            <div className="vault-impact-items">{items.join(', ')}</div></>
        : head}
    </div>
  );
}

/**
 * What a delete would really destroy, after its opening line: the files that
 * die with it (eight named, a count for the rest), the ones that survive
 * elsewhere, and the agents and people who lose reach. The folder-delete
 * confirmation and the multi-select one say it in exactly these words.
 */
export function DeleteTruthRows({ impact, t }: {
  impact: FolderDeleteImpact;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const forever = impact.files_deleted_forever;
  return (
    <>
      {forever.length > 0 && (
        <ImpactRow
          tone="lose"
          head={t('vault.deleteTruth.forever', { n: forever.length })}
          items={[
            ...forever.slice(0, 8).map((f) => f.name),
            ...(forever.length > 8
              ? [t('vault.deleteTruth.andMore', { n: forever.length - 8 })] : []),
          ]}
        />
      )}
      {impact.files_kept_elsewhere > 0 && (
        <ImpactRow tone="keep" head={t('vault.deleteTruth.keptElsewhere', {
          n: impact.files_kept_elsewhere,
        })} />
      )}
      {impact.agents_lose.length > 0 && (
        <ImpactRow
          tone="lose"
          head={t('vault.deleteTruth.agentsLose', { n: impact.agents_lose.length })}
          items={impact.agents_lose.map((s) => subjectLabel(s, t))}
        />
      )}
      {impact.people_lose_share.length > 0 && (
        <ImpactRow
          tone="lose"
          head={t('vault.deleteTruth.peopleLose', { n: impact.people_lose_share.length })}
          items={impact.people_lose_share.map(personLabel)}
        />
      )}
    </>
  );
}

/** A person, named the way they would recognise themselves. */
export const personLabel = (p: ImpactPerson) => p.name || p.email || p.user_id;

/**
 * An agent-side reader: its name and what kind of thing it is. The
 * organization's own AI has no name of its own, so its kind speaks for it.
 */
export const subjectLabel = (s: ImpactSubject, t: (k: string) => string) => {
  const kind = t(`vault.impact.kind.${s.kind}`);
  return s.name ? `${s.name} · ${kind}` : kind;
};
